import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip as BootstrapTooltip, OverlayTrigger } from 'react-bootstrap';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { AiOutlineDrag } from "react-icons/ai";
import { BiTrendingDown, BiTrendingUp } from 'react-icons/bi';
import {
  FiActivity,
  FiBarChart,
  FiInfo,
  FiPieChart,
  FiRefreshCw,
  FiTrendingUp
} from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { RegulationContext } from '../../../App';
import ChartMenu from '../../../components/ChartMenu';
import { GRAPH_FRACTION_DIGITS, LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY } from '../../../core/constants/common';
import useHasPermission from '../../../core/hooks/useHasPermission';
import DashGraphConfigService from '../../../core/service/DashGraphConfigService';
import { formatFyChartXAxisLabel } from '../../../core/utilities/assessmentPeriodUtils';
import Utils from '../../../core/utilities/Utils';
import './ChartMaximizeModal.scss';
import './ModernChartCard.scss';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const ModernChartCard = ({
  config,
  year,
  renderedOn,
  title,
  type = 'LINE',
  height = 400,
  showLegend = true,
  showGrid = true,
  showTooltip = true,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'],
  onMaximize,
  onTableViewModal,
  onDeactivate,
  showLabels: showLabelsProp,
  onShowLabelsChange,
  isMaximizedView = false,
  isModalSized = false,
  bare = false
}) => {
  const { t, i18n } = useTranslation();
  const { glbRegulation } = useContext(RegulationContext);
  // Add chart ref for download functionality
  const chartRef = useRef(null);

  // Helper function to get timeInterval from config (can be at config.timeInterval or config.properties?.options?.timeInterval)
  const getTimeIntervalFromConfig = (configObj) => {
    return configObj?.timeInterval || configObj?.properties?.options?.timeInterval || 'MONTHLY';
  };

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [internalShowLabels, setInternalShowLabels] = useState(false);
  const isShowLabelsControlled = showLabelsProp !== undefined;
  const showLabels = isShowLabelsControlled ? showLabelsProp : internalShowLabels;
  const [showTableView, setShowTableView] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [chartType, setChartType] = useState(type);
  const [yAxisMax, setYAxisMax] = useState(null);
  const [kpiUom, setKpiUom] = useState('');
  //const [activeInterval, setActiveInterval] = useState('YEARLY');
  // Initialize activeInterval from config.timeInterval dynamically, default to 'MONTHLY' if not found
  const [activeInterval, setActiveInterval] = useState(() => getTimeIntervalFromConfig(config));
  const [kpiColor, setKpiColor] = useState('');
  const [graphData, setGraphData] = useState(null);
  // Summary data state
  const [summaryData, setSummaryData] = useState({
    total: 0,
    percentageChange: 0,
    maxMonth: '',
    minMonth: '',
    unit: '',
    showComparison: false
  });

  // Get user context
  const authority = JSON.parse(localStorage.getItem('authority'));
  const companyId = authority?.profile?.company?.id;
  // Financial year: show data in incoming order (no calendar-month sort)
  const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
  const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
  const isFinancialYear = dataFreqType === 'FY';
  const getYearlyXAxisLabel = (dp) =>
    formatFyChartXAxisLabel(dp.xValue, dp.year, isFinancialYear);
  const standardId = glbRegulation?.id;
  const { hasDashboardUpdatePermission } = useHasPermission();

  const configId = config?.id;
  const configType = config?.type;
  const configYearOffset = config?.yearOffset ?? null;

  const intervalFromConfig = useMemo(() => {
    if (!config) return 'MONTHLY';
    return getTimeIntervalFromConfig(config);
  }, [configId, config?.timeInterval, config?.properties?.options?.timeInterval]);

  useEffect(() => {
    setActiveInterval(intervalFromConfig);
  }, [intervalFromConfig]);

  useEffect(() => {
    if (configId && companyId && standardId && year) {
      fetchChartData(activeInterval, configYearOffset);
    } else {
      setIsLoading(false);
    }
  }, [configId, configType, companyId, standardId, year, renderedOn, activeInterval, configYearOffset]);

  useEffect(() => {
    processChartData(graphData);
  }, [showLabels]);

  const fetchChartData = async (interval = '', yearOffset = null) => {
    if (!config?.id || !companyId || !standardId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // For KPI charts, always use current year only (no yearOffset)
      // For other charts, use the provided yearOffset
      const isKPIChart = config.type === 'KPI';
      const finalYearOffset = isKPIChart ? null : yearOffset;
      
      // For YEARLY view, we need to fetch data for multiple years
      // For MONTHLY view, we use the selected year
      // The period is the same for both intervals; the interval parameter controls how data is aggregated
      const period = `${year}-01-01`;

      // Fetch chart data using the same API as CompanyGraphCard
      const response = await DashGraphConfigService.getGraph(
        config.id,
        period,
        standardId,
        null, // businessUnitId
        interval, // interval parameter
        finalYearOffset // yearOffset parameter - null for KPI charts
      );

      if (response.status === 200 && response.data) {
        // Use the same response structure as CompanyGraphCard
        const graphData = response.data.data || response.data;
        setGraphData(graphData);

        // Process the data using the interval we requested (avoids stale activeInterval when toggling Yearly → Monthly)
        processChartData(graphData, interval);
      } else {
        throw new Error('No data received from API');
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setError(t("errorLoadingChartData.label"));

      // Generate sample data as fallback
      generateSampleData();
    } finally {
      setIsLoading(false);
    }
  };

  // Download functionality similar to useBasicGraphCard
  const downloadChartImage = (graphTitle) => {
    const chartInstance = chartRef.current;
    if (chartInstance) {
      const chart = chartInstance.canvas;
      const context = chart.getContext('2d');
      
      // Save the current canvas state
      context.save();
      
      // Create a new canvas with extra space for title and total
      const downloadCanvas = document.createElement('canvas');
      const downloadContext = downloadCanvas.getContext('2d');
      
      // Set canvas size with extra space for title and total (100px top padding)
      downloadCanvas.width = chart.width;
      downloadCanvas.height = chart.height + 100;
      
      // Fill background with white
      downloadContext.fillStyle = '#ffffff';
      downloadContext.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
      
      // Draw the original chart on the new canvas (offset by 100px to make room for title)
      downloadContext.drawImage(chart, 0, 100);
      
      // Add title and total text
      const titleText = graphTitle || title || config?.title || 'Chart';
      const totalText =  `Total: ${formatNumber(summaryData.total)} ${summaryData.unit}`;
      
      // Configure text styling
      downloadContext.fillStyle = '#1e293b'; // Dark text color
      downloadContext.textAlign = 'center';
      downloadContext.textBaseline = 'top';
      
      // Draw title
      downloadContext.font = 'bold 26px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
      downloadContext.fillText(titleText, downloadCanvas.width / 2, 20);
      
      // Draw total
      downloadContext.font = '22px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
      downloadContext.fillText(totalText, downloadCanvas.width / 2, 55);
      
      // Convert to image and download
      const url = downloadCanvas.toDataURL('image/jpeg', 0.9);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${graphTitle || 'chart'}.jpg`;
      link.click();
      
      // Restore original canvas state
      context.restore();
    }
  };

  const downloadChartImageMaximized = (graphTitle) => {
    const chartInstance = chartRef.current;
    if (chartInstance) {
      const originalWidth = chartInstance.width;
      const originalHeight = chartInstance.height;

      // Resize chart for download
      chartInstance.resize(1200, 800);
      const chart = chartInstance.canvas;
      const context = chart.getContext('2d');
      
      // Save the current canvas state
      context.save();
      
      // Create a new canvas with extra space for title and total
      const downloadCanvas = document.createElement('canvas');
      const downloadContext = downloadCanvas.getContext('2d');
      
      // Set canvas size with extra space for title and total (100px top padding)
      downloadCanvas.width = chart.width;
      downloadCanvas.height = chart.height + 100;
      
      // Fill background with white
      downloadContext.fillStyle = '#ffffff';
      downloadContext.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
      
      // Draw the original chart on the new canvas (offset by 100px to make room for title)
      downloadContext.drawImage(chart, 0, 100);
      
      // Add title and total text
      const titleText = graphTitle || title || config?.title || 'Chart';
      const totalText =  `Total: ${formatNumber(summaryData.total)} ${summaryData.unit}`;
      
      // Configure text styling
      downloadContext.fillStyle = '#1e293b'; // Dark text color
      downloadContext.textAlign = 'center';
      downloadContext.textBaseline = 'top';
      
      // Draw title
      downloadContext.font = 'bold 26px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
      downloadContext.fillText(titleText, downloadCanvas.width / 2, 20);
      
      // Draw total
      downloadContext.font = '22px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
      downloadContext.fillText(totalText, downloadCanvas.width / 2, 55);
      
      // Convert to image and download
      const url = downloadCanvas.toDataURL('image/jpeg', 0.9);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${graphTitle || 'chart'}.jpg`;
      link.click();
      
      // Restore original chart size
      context.restore();
      chartInstance.resize(originalWidth, originalHeight);
    }
  };

  const processChartData = (graphData, intervalUsedForFetch = null) => {
    // Use the interval we actually requested when available (fixes Month order when toggling Yearly → Monthly). Fall back to activeInterval if passed value is empty or invalid.
    const intervalForSort = (intervalUsedForFetch === 'MONTHLY' || intervalUsedForFetch === 'YEARLY') ? intervalUsedForFetch : activeInterval;

    // Handle different response structures
    let processedData = graphData;

    // If the response has a nested data structure like CompanyGraphCard expects
    if (graphData && graphData.properties) {
      // This is the format that CompanyGraphCard uses
      processedData = graphData;
    } else if (graphData && graphData.dataPoints) {
      // This is the direct data format
      processedData = graphData;
    } else {
      generateSampleData();
      return;
    }

    if (!processedData || !processedData.dataPoints || processedData.dataPoints.length === 0) {
      generateSampleData();
      return;
    }

    // Process footer data for summary section
    if (processedData.footers && processedData.footers.length > 0) {
      processFooterData(processedData.footers);
    }

    const chartType = config.type || 'BAR';

    switch (chartType) {
      case 'LINE':
        setupLineChartData(processedData, intervalForSort);
        break;
      case 'AREA':
        setupAreaChartData(processedData, intervalForSort);
        break;
      case 'BAR':
      case 'STACKED BAR':
      case 'HORIZONTAL BAR':
      case 'HORIZONTAL STACKED BAR':
        setupBarChartData(processedData, intervalForSort);
        break;
      case 'PIE':
      case 'DOUGHNUT':
        setupPieChartData(processedData);
        break;
      case 'KPI':
        setupKPIChartData(processedData);
        break;
      default:
        setupBarChartData(processedData, intervalForSort);
    }
  };

  const setupLineChartData = (graphData, intervalForSort = null) => {
    const rawPoints = graphData.dataPoints || [];
    const sortInterval = intervalForSort != null ? intervalForSort : activeInterval;
    const useMonthOrder = (sortInterval === 'MONTHLY' || isMonthyInterval(graphData)) && !isFinancialYear;
    const dataPoints = useMonthOrder
      ? sortDataPointsByMonthAscending(rawPoints)
      : [...rawPoints].sort((a, b) => a.order - b.order);
    const dataPointOptions = graphData.dataPointOptions?.filter(o => !o.position) || [];

    const labels = [];
    const datasets = [];

    dataPoints?.forEach(dp => {
      if (isMonthlyChartView(graphData, sortInterval)) {
        labels.push(getMonthlyXAxisLabel(dp));
      } else {
        labels.push(getYearlyXAxisLabel(dp));
      }
    });

    dataPointOptions.forEach((option, index) => {
      const color = Utils.getDecodedHexForGraphColor(option.color) || {
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length] + '20'
      };

      const data = [];
      let translatedLabel = '';

      for (let i = 0; i < dataPoints?.length; i++) {
        const dp = dataPoints[i];
        const value = dp.yValues?.find(y => y.label === option.label)?.value ?? 0;
        const dpCurrentVal = dp.yValues?.find(y => y.label === option.label);
        translatedLabel = getTranslatedLabelWithUnit(dpCurrentVal?.localizedContents, i18n.language, 'label', option.label, option.unitOfMeasure);

        
        if (isMonthyInterval(graphData)) {
          if (year == new Date().getFullYear()) {
            if (i <= new Date().getMonth()) {
              data.push(value);
            }
          } else {
            data.push(value);
          }
        } else {
          // For YEARLY view, include all data points
          data.push(value);
        }
      }

      datasets.push({
        label: translatedLabel,
        data: data,
        borderColor: color.borderColor,
        backgroundColor: color.borderColor,
        borderWidth: 3,
        fill: config?.fill || false,
        tension: 0.4,
        pointBackgroundColor: "transparent",
        pointBorderColor: color.borderColor,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      });
    });

    // Reverse labels and datasets for YEARLY intervals to show years in ascending order (2022-2025)
    const shouldReverseYears = sortInterval === 'YEARLY' || (!isMonthyInterval(graphData) && sortInterval !== 'MONTHLY');
    if (shouldReverseYears) {
      labels.reverse();
      datasets.forEach(dataset => {
        dataset.data.reverse();
      });
    }

    setChartData({ labels, datasets });
    setChartType(config?.type || 'LINE');
    setYAxisMax(calculateYAxisMax({ labels, datasets }));

    // Calculate and set summary data only if footer data is not available
    if (!graphData.footers || graphData.footers.length === 0) {
      const summary = calculateSummaryData({ labels, datasets });
      setSummaryData(summary);
    }
  };

  const setupBarChartData = (graphData, intervalForSort = null) => {
    const rawPoints = graphData.dataPoints || [];
    const sortInterval = intervalForSort != null ? intervalForSort : activeInterval;
    const useMonthOrder = (sortInterval === 'MONTHLY' || isMonthyInterval(graphData)) && !isFinancialYear;
    const dataPoints = useMonthOrder
      ? sortDataPointsByMonthAscending(rawPoints)
      : [...rawPoints].sort((a, b) => a.order - b.order);
    const dataPointOptions = graphData.dataPointOptions?.filter(o => !o.position) || [];

    const labels = [];
    const datasets = [];

    dataPoints?.forEach(dp => {
      if (isMonthlyChartView(graphData, sortInterval)) {
        labels.push(getMonthlyXAxisLabel(dp));
      } else {
        labels.push(getYearlyXAxisLabel(dp));
      }
    });

    dataPointOptions.forEach((option, index) => {
      const color = Utils.getDecodedHexForGraphColor(option.color) || {
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length]
      };

      const data = [];
      let translatedLabel = '';

      for (let i = 0; i < dataPoints?.length; i++) {
        const dp = dataPoints[i];
        const value = dp.yValues?.find(y => y.label === option.label)?.value ?? 0;
        const dpCurrentVal = dp.yValues?.find(y => y.label === option.label);
        translatedLabel = getTranslatedLabelWithUnit(dpCurrentVal?.localizedContents, i18n.language, 'label', option.label, option.unitOfMeasure);

        if (isMonthyInterval(graphData)) {
          if (year == new Date().getFullYear()) {
            if (i <= new Date().getMonth()) {
              data.push(value);
            }
          } else {
            data.push(value);
          }
        } else {
          // For YEARLY view, include all data points
          data.push(value);
        }
      }

      datasets.push({
        label: translatedLabel,
        data: data,
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            return color.backgroundColor;
          }

          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
          gradient.addColorStop(0, color.backgroundColor);
          gradient.addColorStop(1, color.backgroundColor + '90'); // Lighter version for gradient
          return gradient;
        },
        borderColor: 'transparent',
        borderWidth: 0,
        borderRadius: 3,
        stack: config.type?.includes('STACKED') ? 'stack' : undefined,
      });
    });

    // Reverse labels and datasets for YEARLY intervals to show years in ascending order (2022-2025)
    const shouldReverseYears = sortInterval === 'YEARLY' || (!isMonthyInterval(graphData) && sortInterval !== 'MONTHLY');
    if (shouldReverseYears) {
      labels.reverse();
      datasets.forEach(dataset => {
        dataset.data.reverse();
      });
    }

    setChartData({ labels, datasets });
    setChartType(config?.type || 'BAR');
    setYAxisMax(calculateYAxisMax({ labels, datasets }));
      
      // Chart options for horizontal bar charts are now handled in getChartOptions function
    
    // Calculate and set summary data for regular charts
    if (!graphData.footers || graphData.footers.length === 0) {
      const summary = calculateSummaryData({ labels, datasets });
      setSummaryData(summary);
    }
  };

  const setupPieChartData = (graphData) => {
    const dataPoints = graphData.dataPoints?.[0] || {};
    const yValues = dataPoints.yValues || [];

    const labels = yValues.map(y => getTranslatedLabelWithUnit(y?.localizedContents, i18n.language, 'label', y.label, y.unitOfMeasure));
    const data = yValues.map(y => y.value);
    const backgroundColors = yValues.map((_, index) => colors[index % colors.length]);
    const dataPointOptions = graphData.dataPointOptions?.filter(o => !o.position) || [];
    dataPointOptions.forEach((option, index) => {
      const color = Utils.getDecodedHexForGraphColor(option.color);
      backgroundColors[index] = color.backgroundColor;
    });

    setChartData({
      labels,
      xValue: formatFyChartXAxisLabel(dataPoints.xValue || '', dataPoints.year, isFinancialYear),
      datasets: [{
        data: data,
        backgroundColor: backgroundColors,
        borderColor: backgroundColors.map(color => color + '80'),
        borderWidth: 2,
      }]
    });

    setChartType(config.type || 'PIE');
    setYAxisMax(calculateYAxisMax({ labels, datasets: [{ data: data, backgroundColor: backgroundColors }] }));

    // Calculate and set summary data only if footer data is not available
    if (!graphData.footers || graphData.footers.length === 0) {
      const summary = calculateSummaryData({ labels, datasets: [{ data: data, backgroundColor: backgroundColors }] });
      setSummaryData(summary);
    }
  };

  const setupKPIChartData = (graphData) => {
    // For KPI, we'll show the sum of all data points as a large number
    const dataPoints = graphData.dataPoints || [];
    const dataPointOptions = graphData.dataPointOptions?.filter(o => !o.position) || [];

    // Extract UOM from dataPointOptions
    const uom = graphData.dataPointOptions?.[0]?.unitOfMeasure || '';
    setKpiUom(uom);

    // Extract color from dataPointOptions
    const color = Utils.getDecodedHexForGraphColor(dataPointOptions[0]?.color);
    setKpiColor(color);

    if (dataPoints.length > 0 && dataPointOptions.length > 0) {
      // Calculate the sum of all data points for the first dataPointOption
      let totalValue = 0;
      let translatedLabel = '';

      // For KPI charts, always use current year data only
      // Since we're already fetching only current year data in fetchChartData,
      // we can simply sum all the data points without year filtering
      dataPoints.forEach((dp) => {
        if (dp.yValues && dp.yValues.length > 0) {
          const value = dp.yValues.find(y => y.label === dataPointOptions[0].label)?.value ?? 0;
          totalValue += value;
        }
      });

      // Get the label from the first dataPointOption
      translatedLabel = getTranslatedLabelWithUnit(dataPointOptions[0]?.localizedContents, i18n.language, 'label', dataPointOptions[0].label, dataPointOptions[0].unitOfMeasure);

      const chartData = {
        labels: [translatedLabel],
        datasets: [{
          data: [totalValue],
          backgroundColor: colors[0],
          borderColor: colors[0],
        }]
      };

      setChartData(chartData);
      setYAxisMax(calculateYAxisMax(chartData));

      // Calculate and set summary data only if footer data is not available
      if (!graphData.footers || graphData.footers.length === 0) {
        const summary = calculateSummaryData({ labels: [translatedLabel], datasets: [{ data: [totalValue], backgroundColor: colors[0], borderColor: colors[0] }] });
        setSummaryData(summary);
      }
    } else {
      // Fallback for no data
      const fallbackData = {
        labels: ['No Data'],
        datasets: [{ data: [0], backgroundColor: colors[0], borderColor: colors[0] }]
      };
      setChartData(fallbackData);
      setYAxisMax(calculateYAxisMax(fallbackData));

      // Calculate and set summary data only if footer data is not available
      if (!graphData.footers || graphData.footers.length === 0) {
        const summary = calculateSummaryData(fallbackData);
        setSummaryData(summary);
      }
    }
  };

  // Setup AREA chart data (gradient fill under line)
  const setupAreaChartData = (graphData, intervalForSort = null) => {
    const rawPoints = graphData.dataPoints || [];
    const sortInterval = intervalForSort != null ? intervalForSort : activeInterval;
    const useMonthOrder = (sortInterval === 'MONTHLY' || isMonthyInterval(graphData)) && !isFinancialYear;
    const dataPoints = useMonthOrder
      ? sortDataPointsByMonthAscending(rawPoints)
      : [...rawPoints].sort((a, b) => a.order - b.order);
    const dataPointOptions = graphData.dataPointOptions?.filter(o => !o.position) || [];
    const labels = [];
    const datasets = [];
    dataPoints?.forEach(dp => {
      if (isMonthlyChartView(graphData, sortInterval)) {
        labels.push(getMonthlyXAxisLabel(dp));
      } else {
        labels.push(getYearlyXAxisLabel(dp));
      }
    });
    dataPointOptions.forEach((option, index) => {
      // Use a function for backgroundColor to create a gradient
      const color = Utils.getDecodedHexForGraphColor(option.color) || {
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length] + '20'
      };
      const data = [];
      let translatedLabel = '';
      for (let i = 0; i < dataPoints?.length; i++) {
        const dp = dataPoints[i];
        const value = dp.yValues?.find(y => y.label === option.label)?.value ?? 0;
        const dpCurrentVal = dp.yValues?.find(y => y.label === option.label);
        translatedLabel = getTranslatedLabelWithUnit(dpCurrentVal?.localizedContents, i18n.language, 'label', option.label, option.unitOfMeasure);
        if (isMonthyInterval(graphData)) {
          if (year == new Date().getFullYear()) {
            if (i <= new Date().getMonth()) {
              data.push(value);
            }
          } else {
            data.push(value);
          }
        } else {
          // For YEARLY view, include all data points
          data.push(value);
        }
      }
      datasets.push({
        label: translatedLabel,
        data: data,
        borderColor: color.borderColor,
        // Use a function for backgroundColor to create a gradient
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return null;
          const gradient = ctx.createLinearGradient(
            chartArea.left, chartArea.top,
            chartArea.right, chartArea.bottom
          );
          gradient.addColorStop(0, color.borderColor);
          gradient.addColorStop(1, color.backgroundColor);
          return gradient;
        },
        borderWidth: 2,
        fill: true, // Fill area under line
        tension: 0.4,
        pointBackgroundColor: 'transparent',
        pointBorderColor: 'transparent',
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: color.borderColor,
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2,
      });
    });

    // Reverse labels and datasets for YEARLY intervals to show years in ascending order (2022-2025)
    const shouldReverseYears = sortInterval === 'YEARLY' || (!isMonthyInterval(graphData) && sortInterval !== 'MONTHLY');
    if (shouldReverseYears) {
      labels.reverse();
      datasets.forEach(dataset => {
        dataset.data.reverse();
      });
    }

    setChartData({ labels, datasets });
    setChartType('AREA');
    setYAxisMax(calculateYAxisMax({ labels, datasets }));

    // Calculate and set summary data only if footer data is not available
    if (!graphData.footers || graphData.footers.length === 0) {
      const summary = calculateSummaryData({ labels, datasets });
      setSummaryData(summary);
    }
  };

  // Get chart icon based on type
  const getChartIcon = (type) => {
    const icons = {
      'LINE': <FiTrendingUp />,
      'BAR': <FiBarChart />,
      'STACKED BAR': <FiBarChart />,
      'HORIZONTAL BAR': <FiBarChart />,
      'HORIZONTAL STACKED BAR': <FiBarChart />,
      'PIE': <FiPieChart />,
      'DOUGHNUT': <FiPieChart />,
      'AREA': <FiActivity />,
      'COMPOSED': <FiBarChart />,
      'KPI': <FiActivity />
    };
    return icons[type] || <FiBarChart />;
  };

  // Chart menu handlers
  const handleGetInfo = () => {
  };

  // Prepare chart info for the modal
  const chartInfo = {
    name: title || config?.title || 'Chart',
    type: type || 'Chart',
    description: config?.description
  };

  const handleToggleLabels = () => {
    const nextShowLabels = !showLabels;
    if (onShowLabelsChange) {
      onShowLabelsChange(nextShowLabels);
    } else {
      setInternalShowLabels(nextShowLabels);
    }
  };

  const handleToggleView = () => {
    // Toggle between MONTHLY and YEARLY intervals
    const newInterval = activeInterval === 'YEARLY' ? 'MONTHLY' : 'YEARLY';
    setActiveInterval(newInterval);

    // Calculate yearOffset for YEARLY interval (similar to CompanyGraphCard)
    const yearOffset = newInterval === 'YEARLY'
      ? (config?.properties?.options?.timeInterval === 'MONTHLY' ? 3 : config?.properties?.options?.yearOffset ?? 3)
      : 3;

    // Fetch new data with the updated interval
    fetchChartData(newInterval, yearOffset);
  };

  const handleTableViewModal = () => {
    if (onTableViewModal && chartData) {
      onTableViewModal(chartData, config, title);
    } else {
      setShowTableView(!showTableView);
    }
  };

  const handleMaximize = () => {
    if (onMaximize) onMaximize();
  };

  const handleDataSources = () => {
  };

  const handleDownload = () => {
    const graphTitle = title || config?.title || 'chart';
    downloadChartImageMaximized(graphTitle);
  };

  const handleDeactivate = () => {
    if (onDeactivate) {
      onDeactivate();
    }
  };

  const handleRefresh = () => {
    // Calculate yearOffset for current interval
    const yearOffset = activeInterval === 'YEARLY'
      ? (config?.properties?.options?.timeInterval === 'MONTHLY' ? 3 : config?.properties?.options?.yearOffset ?? 3)
      : 3;

    fetchChartData(activeInterval, yearOffset);
  };

  const calculateYAxisMax = (data) => {
    if (!data || !data.datasets) return null;

    let maxValue = 0;

    // Check if this is a stacked chart by looking for stack property in datasets
    const isStacked = data.datasets.some(dataset => dataset.stack !== undefined && dataset.stack !== null);

    if (isStacked) {
      // For stacked charts, calculate the sum at each index position across all datasets
      const numDataPoints = data.datasets[0]?.data?.length || 0;
      
      for (let i = 0; i < numDataPoints; i++) {
        let sum = 0;
        data.datasets.forEach(dataset => {
          if (dataset.data && Array.isArray(dataset.data) && i < dataset.data.length) {
            const value = dataset.data[i];
            if (value !== null && value !== undefined && !isNaN(value)) {
              sum += parseFloat(value);
            }
          }
        });
        maxValue = Math.max(maxValue, sum);
      }
    } else {
      // For non-stacked charts, find the maximum value across all datasets
      data.datasets.forEach(dataset => {
        if (dataset.data && Array.isArray(dataset.data)) {
          const validValues = dataset.data.filter(val => val !== null && val !== undefined && !isNaN(val));
          if (validValues.length > 0) {
            maxValue = Math.max(maxValue, Math.max(...validValues));
          }
        }
      });
    }

    if (maxValue > 0) {
      return roundAxisMax(maxValue * 1.15);
    }

    return null;
  };

  const generateSampleData = () => {
    const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const datasets = [{
      label: 'Sample Data',
      data: [65, 59, 80, 81, 56, 55],
      backgroundColor: colors[0],
      borderColor: colors[0],
      borderWidth: 1,
    }];

    const chartData = { labels, datasets };
    setChartData(chartData);

    // Calculate and set summary data for sample data only if footer data is not available
    const summary = calculateSummaryData(chartData);
    setSummaryData(summary);

    setError(null);
  };

  // Function to transform object with localized content (like CompanyGraphCard)
  const transformObject = (obj) => {
    const transformedObj = { ...obj };
    // Add the translated values to the main object based on languageCode
    if (obj.localizedContents && obj.localizedContents.length > 0) {
      obj.localizedContents.forEach((item) => {
        if (item.fieldName === 'title') {
          transformedObj[item.languageCode] = item.translatedValue;
        } else if (item.fieldName === 'description') {
          transformedObj[`${item.languageCode}_description`] = item.translatedValue;
        }
      });
    }
    return transformedObj;
  };

  // Function to get localized label for a given field (following useBasicGraphCard pattern)
  const getLocalizedLabel = (localizedContents, languageCode, fieldName) => {
    if (!Array.isArray(localizedContents)) return null;
    const localized = localizedContents.find(
      (content) => content.languageCode === languageCode && content.fieldName === fieldName
    );
    return localized ? localized.translatedValue : null;
  };

  // Helper function to append unit to translated label
  const getTranslatedLabelWithUnit = (localizedContents, languageCode, fieldName, fallbackLabel, unit) => {
    const translatedLabel = getLocalizedLabel(localizedContents, languageCode, fieldName) ?? fallbackLabel;
    return unit ? `${translatedLabel} (${unit})` : translatedLabel;
  };

  // Helper functions like CompanyGraphCard
  const isMonthyInterval = (g) => {
    return g?.timeInterval === 'MONTHLY' || g?.properties?.options?.timeInterval === 'MONTHLY';
  };

  // Get calendar month index (0–11) from a data point's xValue. Handles month names, short names, and numeric 1–12.
  const getMonthIndexFromXValue = (xValue) => {
    if (xValue == null || xValue === '') return -1;
    const s = String(xValue).trim();
    const num = parseInt(s, 10);
    if (!isNaN(num) && num >= 1 && num <= 12) return num - 1; // 1=Jan -> 0, 12=Dec -> 11
    const date = new Date(`${s} 1, 2024`);
    if (!isNaN(date.getTime())) return date.getMonth();
    return -1;
  };

  // Sort dataPoints by calendar month (Jan=0 .. Dec=11) so Month View x-axis is always ascending. Uses UI state (activeInterval) so order is correct after refresh.
  const sortDataPointsByMonthAscending = (dataPoints) => {
    if (!dataPoints || dataPoints.length === 0) return dataPoints;
    return [...dataPoints].sort((a, b) => {
      const monthA = getMonthIndexFromXValue(a.xValue);
      const monthB = getMonthIndexFromXValue(b.xValue);
      if (monthA >= 0 && monthB >= 0) return monthA - monthB;
      if (monthA >= 0) return -1;
      if (monthB >= 0) return 1;
      return (a.order ?? 0) - (b.order ?? 0);
    });
  };

  const isYearlyInterval = (g) => {
    return g?.timeInterval === 'YEARLY' || g?.properties?.options?.timeInterval === 'YEARLY';
  };

  // Helper functions for chart options (like CompanyGraphCard.js)
  const getLegendAlign = (g) => {
    const splitLegendPos = g?.properties?.options?.legendPosition?.split(' ');
    const legendAlign = splitLegendPos?.[1] === 'LEFT'
      ? 'start' : splitLegendPos?.[1] === 'RIGHT'
        ? 'end' : 'center';
    return legendAlign;
  };

  const getLegendPosition = (g) => {
    const splitLegendPos = g?.properties?.options?.legendPosition?.split(' ');
    const legendPosition = splitLegendPos?.[0] === 'TOP' ? 'top' : 'bottom';
    return legendPosition;
  };

  const roundChartNumericValue = (value) =>
    parseFloat(Utils._roundNumericValue(parseFloat(value), GRAPH_FRACTION_DIGITS));

  const sumStackedValues = (values) => {
    const multiplier = Math.pow(10, GRAPH_FRACTION_DIGITS);
    const scaledTotal = values.reduce((sum, val) => {
      if (val === null || val === undefined || isNaN(val)) return sum;
      return sum + Math.round(parseFloat(val) * multiplier);
    }, 0);
    return roundChartNumericValue(scaledTotal / multiplier);
  };

  // Helper function to format numbers with comma separators
  // If decimal places are all zeros, display without decimals, otherwise show graph fraction digits
  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }

    const rounded = roundChartNumericValue(value);
    const decimalPart = Math.abs(rounded % 1);
    const isAllZeros = decimalPart < Math.pow(10, -(GRAPH_FRACTION_DIGITS + 1));

    if (Number.isInteger(rounded) || isAllZeros) {
      return rounded.toLocaleString('en-US');
    }

    return rounded.toLocaleString('en-US', {
      minimumFractionDigits: GRAPH_FRACTION_DIGITS,
      maximumFractionDigits: GRAPH_FRACTION_DIGITS
    });
  };

  // Helper function to format numbers properly - comma separators and 4 decimals for fractional
  // If decimal places are all zeros, display without decimals
  const formatNumber = (value) => {
    return formatNumberWithCommas(value);
  };

  // Calculate summary data for the chart
  const calculateSummaryData = (data) => {
    if (!data || !data.datasets || data.datasets.length === 0) {
      return {
        total: 0,
        percentageChange: 0,
        maxMonth: '',
        minMonth: '',
        unit: kpiUom || 'tons',
        comparisonStyle: 'HIGHER THE BETTER', // Default comparison style
        hasIncreased: false,
        showComparison: false
      };
    }

    // Calculate total from all datasets
    let total = 0;
    const allData = [];

    data.datasets.forEach(dataset => {
      if (dataset.data && Array.isArray(dataset.data)) {
        dataset.data.forEach((value, index) => {
          if (value !== null && value !== undefined && !isNaN(value)) {
            total += value;
            allData.push({ value, month: data.labels[index] });
          }
        });
      }
    });

    // Find max and min months
    let maxMonth = '';
    let minMonth = '';
    let maxValue = -Infinity;
    let minValue = Infinity;

    allData.forEach(item => {
      if (item.value > maxValue) {
        maxValue = item.value;
        maxMonth = item.month;
      }
      if (item.value < minValue) {
        minValue = item.value;
        minMonth = item.month;
      }
    });

    // Calculate percentage change (simplified - in real app this would compare with previous year)
    // For now, we'll use a mock calculation based on the data variance
    const percentageChange = allData.length > 1 ?
      ((maxValue - minValue) / minValue * 100).toFixed(1) : 0;

    // Determine if the trend is positive based on comparison style
    // For ESG metrics, typically "HIGHER THE BETTER" means emissions should be lower (negative is good)
    // and "LOWER THE BETTER" means emissions should be lower (negative is good)
    const comparisonStyle = config?.comparisonStyle || 'HIGHER THE BETTER';
    const hasIncreased = parseFloat(percentageChange) > 0;

    return {
      total: total, // Don't round here, let the formatting function handle it
      percentageChange: parseFloat(percentageChange),
      maxMonth,
      minMonth,
      unit: kpiUom || 'tons',
      comparisonStyle,
      hasIncreased,
      showComparison: false
    };
  };

  // Process footer data from API response
  const processFooterData = (footers) => {
    if (!footers || footers.length === 0) return;

    // Find the footer with BOTTOM LEFT position for total value
    const totalFooter = footers.find(f => f.position === 'BOTTOM LEFT');
    // Find the footer with BOTTOM RIGHT position for comparison
    const comparisonFooter = footers.find(f => f.position === 'BOTTOM RIGHT');

    if (totalFooter) {
      const summaryData = {
        total: parseFloat(totalFooter.value) || 0, // Ensure it's a number, not rounded
        unit: totalFooter.uom || kpiUom || 'tons',
        maxMonth: '',
        minMonth: '',
        percentageChange: 0,
        comparisonStyle: 'HIGHER THE BETTER',
        hasIncreased: false,
        showComparison: false
      };

      // Check if comparison should be shown based on BOTTOM RIGHT footer's comparison status
      // The BOTTOM LEFT footer's comparison status is only for the total value, not for showing comparison percentage
      if (comparisonFooter && comparisonFooter.comparison === true) {
        // We have a comparisonFooter with comparison enabled, show the comparison percentage
        const currentValue = comparisonFooter.currentValue || 0;
        const comparedValue = comparisonFooter.comparedValue || 0;
        const hasIncreased = currentValue > comparedValue;

        // Use the value field directly from footer (this is the percentage)
        const percentageChange = comparisonFooter.value || 0;

        summaryData.percentageChange = parseFloat(percentageChange);
        summaryData.hasIncreased = hasIncreased;
        summaryData.comparisonStyle = comparisonFooter.comparisonStyle || 'HIGHER THE BETTER';
        summaryData.showComparison = true;
      } else {
        // No comparisonFooter or comparison is false, don't show comparison percentage
        summaryData.showComparison = false;
      }
      setSummaryData(summaryData);
    }
  };

  // Helper function to detect dark theme
  const isDarkTheme = () => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  };
  const isLightTheme = () => {
    return document.documentElement.getAttribute('data-theme') === 'light';
  };

  // Chart.js options
  const getChartOptions = () => {
    const isDark = isDarkTheme();
    const isLight = isLightTheme();
    const labelColor = isDark ? '#ffffff' : '#424242';
    const dataLabelColor = isDark ? '#ffffff' : '#374151';
    const tooltipBgColor = isDark ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)';
    const tooltipTitleColor = isDark ? '#ffffff' : '#1a202c';
    const tooltipBodyColor = isDark ? '#e5e7eb' : '#374151';
    const tooltipBorderColor = 'rgba(255, 255, 255, 0.3)';

    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 10,
          right: 10,
          bottom: 10,
          left: 10
        }
      },
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          onClick: null,  
          labels: {
            
            usePointStyle: true,
            pointStyle: 'rect',
            padding: 10,
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 13,
            },
            color: labelColor,
          }
        },
        datalabels: {
          display: showLabels,
          color: dataLabelColor,
          font: {
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 11,
            weight: '600',
          },
          anchor: (chartType === 'LINE') ? 'end' : 'center',
          align: (chartType === 'LINE') ? 'top' : 'center',
          offset: (chartType === 'LINE') ? 4 : 0,
          clamp: (chartType === 'BAR' || chartType === 'STACKED BAR') ? true : false,
          formatter: (value) => {
            if (value === 0) return '';
            // Format properly for all charts except KPI
            if (chartType !== 'KPI') {
              return formatNumberWithCommas(value);
            }
            return value.toLocaleString();
          },
        },
        tooltip: {
          enabled: showTooltip,
          backgroundColor: tooltipBgColor,
          titleColor: tooltipTitleColor,
          bodyColor: tooltipBodyColor,
          borderColor: tooltipBorderColor,
          borderWidth: 1,
          cornerRadius: 12,
          displayColors: true,
          titleFont: {
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 14,
            weight: '600',
          },
          bodyFont: {
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 12,
          },
          footerFont: {
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 12,
            weight: 'bold',
          },
          footerColor: isDark ? '#ffffff' : '#000000',
          padding: 6,
          boxPadding: 6,
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              
              let value;
              if (chartType === 'PIE' || chartType === 'DOUGHNUT') {
                value = context.parsed;
                const data = context.chart?.data?.datasets?.[0]?.data || [];
                const total = data.reduce((sum, v) => sum + (Number(v) || 0), 0);
                if (value !== null && value !== undefined && !isNaN(value) && total > 0) {
                  const pct = (value / total) * 100;
                  label += `${formatNumberWithCommas(value)} (${pct % 1 === 0 ? pct : pct.toFixed(1)}%)`;
                } else {
                  label += formatNumberWithCommas(value ?? 0);
                }
                return label;
              }
              if (chartType === 'HORIZONTAL BAR' || chartType === 'HORIZONTAL STACKED BAR') {
                value = context.parsed.x;
              } else {
                value = context.parsed.y;
              }
              if (value !== null && value !== undefined && !isNaN(value)) {
                if (chartType !== 'KPI') {
                  label += formatNumberWithCommas(value);
                } else {
                  label += value.toLocaleString();
                }
              } else {
                label += '0';
              }
              return label;
            },
            footer: function(context) {
              // Show total for stacked bar charts
              if (chartType === 'STACKED BAR' || chartType === 'HORIZONTAL STACKED BAR') {
                const dataIndex = context[0].dataIndex;
                const datasets = context[0].chart.data.datasets;

                const segmentValues = datasets.map((dataset) => dataset.data[dataIndex]);
                const total = sumStackedValues(segmentValues);
                
                if (total > 0) {
                  // Format the total value
                  let formattedTotal;
                  if (chartType !== 'KPI') {
                    formattedTotal = formatNumber(total);
                  } else {
                    formattedTotal = total.toLocaleString();
                  }                  
                  return `Total: ${formattedTotal}`;
                }
              }
              return '';
            }
          }
        }
      },
      elements: {
        point: {
          hoverRadius: 6,
          hoverBorderWidth: 2,
        }
      }
    };

    // Add specific options for different chart types
    if (chartType === 'HORIZONTAL BAR' || chartType === 'HORIZONTAL STACKED BAR') {
      baseOptions.indexAxis = 'y';
      
      // For horizontal bar charts, configure legend like CompanyGraphCard.js does
      baseOptions.plugins.legend = {
        ...baseOptions.plugins.legend,
        position: config?.overrideLegendPosition ? getLegendPosition(config) : "bottom",
        align: config?.overrideLegendPosition ? getLegendAlign(config) : 'start',
        labels: {
          ...baseOptions.plugins.legend.labels,
          font: {
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 13,
          }
        },
        display: config?.hideLegend ? false : showLegend
      };
      
      // For horizontal bar charts, configure datalabels with center positioning
      baseOptions.plugins.datalabels = {
        ...baseOptions.plugins.datalabels,
        anchor: 'center',
        align: 'center',
        color: isDarkTheme() ? '#fff' : '#000',
        font: {
          family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          size: 11,
          weight: '600',
        },
        formatter: (value) => {
          if (value === 0) return '';
          // Format properly for horizontal bar charts
          return formatNumberWithCommas(value);
        }
      };
      
      // For horizontal bar charts, DON'T override scales - let Chart.js handle Y-axis labels naturally
      // This is the key difference from CompanyGraphCard.js - it doesn't override scales
      
      // CRITICAL: Return early to prevent any other scale configurations from being applied
      return baseOptions;
    }

    // Hide axes for pie and doughnut charts; show percentage on slices
    if (chartType === 'PIE' || chartType === 'DOUGHNUT') {
      baseOptions.scales = {
        x: {
          display: false,
        },
        y: {
          display: false,
        }
      };
      baseOptions.plugins.datalabels = {
        ...baseOptions.plugins.datalabels,
        display: true,
        anchor: 'center',
        align: 'center',
        color: dataLabelColor,
        formatter: (value, context) => {
          if (value === 0) return '';
          const dataset = context.chart?.data?.datasets?.[0];
          const data = dataset?.data || [];
          const total = data.reduce((sum, v) => sum + (Number(v) || 0), 0);
          if (total === 0) return '';
          const pct = (value / total) * 100;
          return pct % 1 === 0 ? `${pct}%` : `${pct.toFixed(1)}%`;
        },
      };
    } else if (chartType === 'STACKED BAR') {
      // Regular stacked bar (vertical)
      // Show individual segment values inside each segment
      // Show total on top of the last segment when showLabels is enabled
      baseOptions.plugins.datalabels = {
        ...baseOptions.plugins.datalabels,
        // Keep all segment values centered inside segments (original behavior)
        anchor: 'center',
        align: 'center',
        offset: 0,
        clamp: true,
        color: dataLabelColor,
        formatter: (value, context) => {
          // Always show individual segment values inside ALL segments (including last one)
          if (value === 0) return '';
          
          // Show individual values for all segments (preserve existing behavior)
          if (chartType !== 'KPI') {
            return formatNumberWithCommas(value);
          }
          return value.toLocaleString();
        },
      };
      
      baseOptions.scales = {
        x: {
          display: true,
          grid: {
            display: showGrid,
            color: (context) => {
              if (context.tick && context.tick.value === 0) {
                return 'rgb(200, 199, 199)'; // Black for first line
              }
              return 'transparent'; // Original color for other lines
            },
            drawBorder: false,
          },
          ticks: {
            color: labelColor,
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 13,
            },
          },
          border: {
            display: false,
          },
          stacked: true
        },
        y: {
          display: true,
          grid: {
            display: showGrid,
            color: (context) => {
              if (context.tick && context.tick.value === 0) {
                return 'rgb(200, 199, 199)'; // Black for first line
              }
              return 'transparent'; // Original color for other lines
            },
            drawBorder: false,
          },

          ticks: {
            color: labelColor,
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 13,
            },
            // callback: function (value, index, values) {
            //   // Format large numbers with K, M, B suffixes
            //   if (value >= 1000000000) {
            //     return (value / 1000000000).toFixed(1) + 'B';
            //   } else if (value >= 1000000) {
            //     return (value / 1000000).toFixed(1) + 'M';
            //   } else if (value >= 1000) {
            //     return (value / 1000).toFixed(1) + 'K';
            //   }
            //   // For y-axis labels, use proper formatting for all charts except KPI
            //   if (chartType !== 'KPI') {
            //     const num = parseFloat(value);
            //     if (Number.isInteger(num)) {
            //       return num.toString();
            //     } else {
            //       return num.toFixed(2);
            //     }
            //   }
              //return value.toLocaleString();
           // }
            callback: function (value, index, values) {
              return Utils.formatYAxisValue(value);
            }
          },
          border: {
            display: false,
          },
          stacked: true,
          beginAtZero: true,
          // Add padding to prevent bars from touching the top
          // Increased padding for maximized view to prevent total text from being cut off
          afterFit: function (scale) {
            scale.paddingTop = isMaximizedView ? 35 : 20;
          },
          // Set appropriate max value based on actual data
          max: yAxisMax
        }
      };
    } else if (chartType === 'HORIZONTAL STACKED BAR') {
      // Horizontal stacked bar - DON'T override scales, let Chart.js handle Y-axis labels naturally
      // This is the key difference from CompanyGraphCard.js - it doesn't override scales for horizontal charts
    } else {
      baseOptions.scales = {
        x: {
          display: true,
          grid: {
            display: showGrid,
            color: (context) => {
              if (context.tick && context.tick.value === 0) {
                return 'rgb(200, 199, 199)'; // Black for first line
              }
              return 'transparent'; // Original color for other lines
            },
            drawBorder: false,
          },
          ticks: {
            color: labelColor,
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 13,
            },
          },
          border: {
            display: false,
          }
        },
        y: {
          display: true,
          grid: {
            display: showGrid,
            color: (context) => {
              if (context.tick && context.tick.value === 0) {
                return 'rgb(200, 199, 199)'; // Black for first line
              }
              return 'transparent'; // Original color for other lines
            },
            drawBorder: false,
          },
          ticks: {
            color: labelColor,
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 13,
            },
            callback: function (value, index, values) {
              return Utils.formatYAxisValue(value);
            }
          },
          border: {
            display: false,
          },
          beginAtZero: true,
          // Add padding to prevent bars from touching the top
          afterFit: function (scale) {
            scale.paddingTop = 20;
          },
          // Set appropriate max value based on actual data
          max: yAxisMax
        }
      };
    }

    return baseOptions;
  };

  // Use useMemo to regenerate chart options when chartData changes
  const chartOptions = React.useMemo(
    () => getChartOptions(),
    [chartData, chartType, showGrid, showLegend, showTooltip, yAxisMax, showLabels, isMaximizedView]
  );

  // Create custom plugin for stacked bar total on top
  // Include chartData in dependencies to ensure plugin updates when data changes
  const stackedBarTotalPlugin = React.useMemo(() => {
    if (chartType !== 'STACKED BAR' || !showLabels) return null;
    
    const isDark = isDarkTheme();
    const dataLabelColor = isDark ? '#ffffff' : '#374151';
    
    return {
      id: 'stackedBarTotal',
      afterDraw: (chart) => {
        // Only process stacked bar charts
        if (!chart || !chart.data || !chart.data.datasets) return;
        
        const ctx = chart.ctx;
        const datasets = chart.data.datasets;
        if (!datasets || datasets.length === 0) return;
        
        const lastDatasetIndex = datasets.length - 1;
        const lastDataset = datasets[lastDatasetIndex];
        if (!lastDataset || !lastDataset.data) return;
        
        // Get the meta for the last dataset
        const meta = chart.getDatasetMeta(lastDatasetIndex);
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        // Check if labels exist
        const labels = chart.data.labels || [];
        if (labels.length === 0) return;
        
        labels.forEach((label, dataIndex) => {
          const segmentValues = datasets.map((dataset) => dataset.data[dataIndex]);
          const total = sumStackedValues(segmentValues);
          
          if (total > 0 && dataIndex < meta.data.length) {
            // Get the bar element for the last dataset at this data index
            const bar = meta.data[dataIndex];
            if (!bar || typeof bar.x === 'undefined' || typeof bar.y === 'undefined') return;
            
            // For vertical stacked bars:
            // bar.y is the y-coordinate of the top of the last segment (which is the top of the entire stack)
            // bar.x is the x-coordinate (center of the bar)
            // bar.base is the bottom of the bar
            const topY = bar.y; // Top of the entire stacked bar
            const x = bar.x; // Center of the bar
            
            // Format the total value
            const formattedTotal = chartType !== 'KPI' 
              ? formatNumberWithCommas(total)
              : total.toLocaleString();
            
            // Draw the total label on top
            ctx.save();
            ctx.font = `600 14px Inter, -apple-system, BlinkMacSystemFont, sans-serif`;
            ctx.fillStyle = dataLabelColor;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            
            // Draw text above the bar (topY is the top, so subtract offset to position above)
            // Increased offset to 30 for maximized view to prevent text from being cut off on long bars
            // Regular view keeps original offset of 10 for backward compatibility
            const offset = isMaximizedView ? 30 : 10;
            ctx.fillText(formattedTotal, x, topY - offset);
            ctx.restore();
          }
        });
      }
    };
  }, [chartType, showLabels, formatNumberWithCommas, chartData, isMaximizedView]);

  // Render chart based on type
  const renderChart = () => {
    if (isLoading) {
      return (
        <div className="chart-loading">
          <FiRefreshCw className="loading-spinner spinning" />
          <p>{t("loadingChart.label")}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="chart-error">
          <p>{error}</p>
          <button onClick={handleRefresh} className="retry-button">
            {t("retry.label")}
          </button>
        </div>
      );
    }

    if (!chartData) {
      generateSampleData();
      return (
        <div className="chart-loading">
          <FiRefreshCw className="loading-spinner spinning" />
          <p>{t("loadingChart.label")}</p>
        </div>
      );
    }

    const currentChartType = config?.type || 'BAR';

    switch (currentChartType) {
      case 'LINE':
        return <Line ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
      case 'AREA':
        return <Line ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
      case 'BAR':
      case 'HORIZONTAL BAR':
      case 'HORIZONTAL STACKED BAR':
        return <Bar ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
      case 'STACKED BAR':
        // For stacked bars, include custom plugin for total on top if showLabels is enabled
        // Always create a new plugins array to ensure Chart.js re-registers plugins on update
        const plugins = [ChartDataLabels];
        if (showLabels && stackedBarTotalPlugin) {
          plugins.push(stackedBarTotalPlugin);
        }
        // Add key to force re-render when data or showLabels changes
        return <Bar ref={chartRef} data={chartData} options={chartOptions} plugins={plugins} height={height} key={`stacked-bar-${showLabels}-${chartData?.labels?.length || 0}`} />;
      case 'PIE':
      // return <Pie ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
      case 'DOUGHNUT':
        return <Doughnut ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
      case 'KPI':
        // New KPI card style: blue gradient, large value/unit, label below
        return (
          <div className="modern-kpi-card 01" style={{ textAlign: 'center', background: kpiColor?.backgroundColor || '' }}>
            <div className="modern-kpi-card-content">


              <div className="modern-kpi-header00">
                <div className="drag-handle KpiHandle">
                  <AiOutlineDrag />
                </div>
                
                {onDeactivate && (() => {
                  // Check user permissions similar to Dashboard.js
                  const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
                  //  const isCompanyAdmin = PermissionUtils.hasCompanyAdminPermission(lsData?.roles); 
                  const isCompanyAdmin = hasDashboardUpdatePermission();

                  return isCompanyAdmin ? (
                    <div
                      className="close-handle KpiCloseHandle"
                      onClick={onDeactivate}
                      title={t("deactivate.label")}
                    >
                      <MdClose />
                    </div>
                  ) : null;
                })()}

              </div>



              <div className="modern-kpi-value-row" style={{ justifyContent: 'center' }}>
                <span className="modern-kpi-value">{formatNumber(chartData.datasets[0]?.data[0] || '0')}</span>
                <span className="modern-kpi-unit">{kpiUom}</span>
              </div>
              <div className="modern-kpi-label" style={{ textAlign: 'center' }}>{chartData.labels[0] || t("kpi.noData.label")}</div>
            </div>
          </div>

        );
      default:
        return <Bar ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
    }
  };

  // If bare, render only the chart and dynamic content, no card/header/footer
  if (bare) {
    return (
      <div className="modern-chart-bare" style={{ width: '100%', height: '100%', background: 'transparent', boxShadow: 'none', padding: 0 }}>
        {/* Optional: Render title and stats if needed, or just the chart */}
        {title && <h3 className="modern-chart-bare-title">{title}</h3>}
        <div className="modern-chart-bare-canvas">
          {renderChart()}
        </div>
      </div>
    );
  }

  const isMonthlyChartView = (graphData, sortInterval) => {
    const interval = sortInterval != null ? sortInterval : activeInterval;
    return interval === 'MONTHLY' || isMonthyInterval(graphData);
  };

  const getMonthlyXAxisLabel = (dp) => {
    if (dp.year != null) {
      return `${dp.xValue} ${dp.year}`;
    }
    return dp.xValue;
  };

  const roundAxisMax = (paddedMax) => {
    if (paddedMax >= 1000000000) return Math.ceil(paddedMax / 50000) * 50000;
    if (paddedMax >= 1000000) return Math.ceil(paddedMax / 50000) * 50000;
    if (paddedMax >= 1000) return Math.ceil(paddedMax / 5000) * 5000;
    if (paddedMax >= 1) return Math.ceil(paddedMax);
    if (paddedMax >= 0.1) return Math.ceil(paddedMax * 10) / 10;
    if (paddedMax >= 0.01) return Math.ceil(paddedMax * 100) / 100;
    if (paddedMax >= 0.001) return Math.ceil(paddedMax * 1000) / 1000;
    return Math.ceil(paddedMax * 10000) / 10000;
  };
  
  return (
    <div className={`modern-chart-card${isModalSized ? ' modern-chart-card--modal' : ''}`}>
      {/* <div className="drag-handle">
        <AiOutlineDrag />
      </div> */}
      {/* Chart Header */}
      {chartType !== 'KPI' && <div className="chart-header">
        <div className="chart-title">
          {/* <div className="chart-icon">
            {getChartIcon(chartType)}
          </div> */}
          <div className="chart-info">
            <h6>{title || t("modernChart.title.label")}</h6>
            {/* <span className="chart-subtitle">{year} • {chartType} • {activeInterval === 'MONTHLY' ? t("Monthly.label") : t("Yearly.label")}</span> */}
          </div>
        </div>
        <div className="chart-actions 12">
          <div className="drag-handle">
            <AiOutlineDrag />
          </div>
          {!isMaximizedView && (
            <ChartMenu
              chartType={chartType}
              chartId={`modern-chart-${title}`}
              onGetInfo={handleGetInfo}
              onToggleLabels={handleToggleLabels}
              onToggleView={handleToggleView}
              onMaximize={handleMaximize}
              onDataSources={handleDataSources}
              onViewTable={handleTableViewModal}
              onDownload={handleDownload}
              onDeactivate={handleDeactivate}
              showLabels={showLabels}
              isMonthView={activeInterval === 'MONTHLY'}
              isMaximized={false}
              showTableView={showTableView}
              chartInfo={chartInfo}
            />
          )}
        </div>
      </div>}
      {/* Chart Content */}
      {chartType !== 'KPI' && <div className="chart-content">
        {/* Chart Summary Section */}
        {chartData && !isLoading && !error && (
          <div className="chart-summary">
            {/* <div className="summary-title">{t("ChartSummary.total.label")}</div> */}
            <div className="summary-metrics">
              <div className="primary-value">
                <span className="value">
                  {formatNumber(summaryData.total)}
                </span>
                <span className="unit">{summaryData.unit}</span>
              </div>
              {summaryData.showComparison && (
                <div className={`percentage-change ${summaryData.comparisonStyle === 'HIGHER THE BETTER' ?
                  (summaryData.hasIncreased ? 'positive' : 'negative') :
                  (summaryData.hasIncreased ? 'negative' : 'positive')}`}>
                  {summaryData.comparisonStyle === 'HIGHER THE BETTER' ?
                    (summaryData.hasIncreased ?
                      <BiTrendingUp className="arrow-icon" /> :
                      <BiTrendingDown className="arrow-icon" />
                    ) :
                    (summaryData.hasIncreased ?
                      <BiTrendingDown className="arrow-icon" /> :
                      <BiTrendingUp className="arrow-icon" />
                    )
                  }
                  <OverlayTrigger
                    placement="auto"
                    overlay={<BootstrapTooltip id="tooltip-like">{t("ChartSummary.thanLastYear.label")}</BootstrapTooltip>} >
                    <span className="percentage">{Math.abs(summaryData.percentageChange)}%</span>
                  </OverlayTrigger>
                  {/* <span className="comparison-text">{t("ChartSummary.thanLastYear.label")}</span> */}
                </div>
              )}
            </div>
            {summaryData.maxMonth && summaryData.minMonth && (
              <div className="summary-info">
                {/* <FiInfo className="info-icon" />
                <span>
                  {t("ChartSummary.maximumInMonth.label", {
                    month: summaryData.maxMonth,
                    minMonth: summaryData.minMonth
                  })}
                </span> */}
                 <span style={{ color: '#dc3545' }}>
                  {t("ChartSummary.checkConfiguration.label")}
                  </span>
              </div>
            )}
          </div>
        )}
        {/* {renderChart()} */}
        <div
          className="chart-canvas-container"
          data-testid="chart-canvas-container"
          onClick={(e) => {
            handleMaximize();
          }}
          style={{ cursor: 'pointer' }}
          title={isMaximizedView ? t("minimize.label") : t("maximize.label")}
        >
          {renderChart()}
        </div>
      </div>}
      {(chartType === 'PIE' || chartType === 'DOUGHNUT') && chartData?.xValue && (
        <div className="text-center" data-testid="chart-card-footer">
          {chartData.xValue}
        </div>
      )}
      {chartType === 'KPI' &&
        // <>
        //   {renderChart()}
        // </>
        <div
          className="chart-kpi-container"
          data-testid="chart-kpi-container"
          onClick={handleMaximize}
          style={{
            width: '100%',
            height: '100%',
            cursor: 'pointer'
          }}
          title={isMaximizedView ? t("minimize.label") : t("maximize.label")}
        >
          {renderChart()}
        </div>
      }

    </div>
  );
};

export default ModernChartCard; 